import { useMemo } from 'react';

import { GridColDef, GridRowsProp } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';

import { EditRemoveBtn } from 'components/buttons';
import { PanelGrid } from 'components/grid';
import { Block } from 'components/page';
import { SubTitle } from 'components/text';
import {
    DetalhamentoCreditosImportacaFilter,
    DetalhamentoCreditosImportacaoStatusColumn,
} from 'pages/financeiro/detalhamento-creditos/importar/components/';
import { useListagemContext } from 'context/ListagemContext';
import { usePermissionsContext } from 'context/PermissionsContext';
import { RequestOptionsType, useRequestListagem } from 'hooks/useRequestListagem';
import { ArquivoProps, RequestArquivosProps } from 'pages/financeiro/detalhamento-creditos/importar/types';
import { brlDateTime } from 'util/format';

const generateImportacaoArquivoList = (data: ArquivoProps[] | undefined): GridRowsProp => {
    if (Array.isArray(data) && data.length) {
        return data.map((item, index) => ({
            id: index + 1,
            idDetalhamentoCreditoDebitoImportacao: item.idDetalhamentoCreditoDebitoImportacao,
            dsArquivo: item.dsArquivo,
            idStatusProcessamento: item.statusProcessamento.idStatusProcessamento,
            dsStatusProcessamento: item.statusProcessamento.dsStatusProcessamento,
            validacoes: item.validacoes,
            dhInclusao: item.dhInclusao,
            peProcessamento: (item.nrTotalLinhaProcessada / item.nrTotalLinha) * 100,
        }));
    }
    return [];
};

export const DetalhamentoCreditosImportacaoGrid = () => {
    const { limit, page, filter, sort } = useListagemContext();
    const RequestListagem = useRequestListagem();
    const { hasPermission } = usePermissionsContext();

    const requestOptions: RequestOptionsType = useMemo(
        () => ({
            url: '/financeiro/detalhamentocreditodebitoimportacao/lista',
            limit,
            page,
            sort: sort.length ? sort : [{ property: 'dhInclusao', direction: 'DESC' }],
            filter,
        }),
        [filter, limit, page, sort],
    );

    const { refetch, data, fetchStatus } = useQuery(['importacao-file-list', requestOptions], () => {
        const request: Promise<RequestArquivosProps> = RequestListagem(requestOptions).then((res) => res.data);
        return request;
    });

    const columns: GridColDef[] = [
        { field: 'idDetalhamentoCreditoDebitoImportacao', headerName: 'ID Arquivo', type: 'number', minWidth: 80 },
        { field: 'dsArquivo', headerName: 'Arquivo', minWidth: 200, flex: 1 },
        {
            field: 'dsStatusProcessamento',
            headerName: 'Status',
            width: 220,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => <DetalhamentoCreditosImportacaoStatusColumn row={params.row} />,
        },
        { field: 'dhInclusao', headerName: 'Data envio', width: 150, minWidth: 140, valueFormatter: (value) => value && brlDateTime(value) },
    ];

    if (hasPermission(['FINANCEIRO_DETALHAMENTO_CREDITO_EXCLUIR'])) {
        columns.push({
            field: 'acoes',
            headerName: 'Cancelar',
            headerAlign: 'center',
            align: 'center',
            minWidth: 90,
            filterable: false,
            sortable: false,
            hideable: false,
            disableColumnMenu: true,
            renderCell: ({ row }) => {
                const { idDetalhamentoCreditoDebitoImportacao, idStatusProcessamento } = row;
                const url = [3, 4, 5].includes(idStatusProcessamento)
                    ? `financeiro/detalhamentocreditodebitoimportacao/${idDetalhamentoCreditoDebitoImportacao}`
                    : undefined;

                return (
                    <EditRemoveBtn id={idDetalhamentoCreditoDebitoImportacao} refresh={refetch} showEditBtn={false} removeUrl={url} sx={{ p: 1.5 }} />
                );
            },
        });
    }

    return (
        <Block id="files-import-grid">
            <SubTitle label="Arquivos de Importação" />

            <DetalhamentoCreditosImportacaFilter />

            <PanelGrid
                rows={generateImportacaoArquivoList(data?.data)}
                rowsCount={data?.total ?? 0}
                reload={refetch}
                loading={fetchStatus === 'fetching'}
                columns={columns}
            />
        </Block>
    );
};
